import { useCallback, useEffect, useRef, useState } from 'react';
import { getActiveCall, ActiveCall } from '../services/incomingCallAPI';

const POLL_MS = 2000;
const DISMISS_KEY = 'rc_dismissed_calls';
// A later call from the same number re-pops after this gap; a transfer's second session id
// (same caller, ~seconds apart) falls WITHIN the gap and stays dismissed - no double-fire.
const REPOP_GAP_MS = 60_000;

type DismissMap = Record<string, string>; // call_key -> received_at ISO it was dismissed at

function loadDismissed(): DismissMap {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveDismissed(m: DismissMap) {
  try {
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify(m));
  } catch {
    /* sessionStorage unavailable - dismissal just won't persist across remount */
  }
}

/**
 * Polls the active-call endpoint every 2s and applies PER-SESSION dismissal (sessionStorage,
 * so a dismissal stays gone on this browser session but still shows on others). Dedups by
 * `call_key` (the caller), so a transferred call spanning two telephonySessionIds pops once.
 */
export function useIncomingCall() {
  const [call, setCall] = useState<ActiveCall | null>(null);
  const dismissedRef = useRef<DismissMap>(loadDismissed());

  const visible = useCallback((c: ActiveCall | null): ActiveCall | null => {
    if (!c) return null;
    const dismissedAt = dismissedRef.current[c.call_key];
    if (!dismissedAt) return c;
    // Re-show only when this is genuinely a newer call, not the tail/transfer-leg of the one
    // that was dismissed.
    const delta = new Date(c.received_at).getTime() - new Date(dismissedAt).getTime();
    return delta > REPOP_GAP_MS ? c : null;
  }, []);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const c = await getActiveCall();
        if (!active) return;
        setCall((prev) => {
          const next = visible(c);
          // Avoid churn/thrash: keep the same object unless the visible call actually changed.
          if (prev?.call_key === next?.call_key && prev?.received_at === next?.received_at) {
            return prev;
          }
          return next;
        });
      } catch {
        /* transient network error - keep last state, don't thrash the panel */
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [visible]);

  const dismiss = useCallback(() => {
    setCall((current) => {
      if (current) {
        dismissedRef.current = {
          ...dismissedRef.current,
          [current.call_key]: current.received_at,
        };
        saveDismissed(dismissedRef.current);
      }
      return null;
    });
  }, []);

  return { call, dismiss };
}
